/* eslint-disable @typescript-eslint/indent */
/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Icon,  LinearProgress, Pagination, Paper, Snackbar, SnackbarCloseReason, Table, TableBody, TableCell, TableContainer, TableFooter, TableHead, TableRow, Box, Typography, useTheme, alpha, Tooltip} from '@mui/material';
import { useSearchParams } from 'react-router-dom';
import { LinhasEsqueletoTabela } from '../../../shared/components';
import { LayoutBaseDePagina } from '../../../shared/layouts';
import { useDebounce } from '../../../shared/hooks';
import { Environment } from '../../../shared/environment';
import { getRowsPerPageCache, setRowsPerPageCache } from '../../../shared/utils/paginaRowsCache';
import { IListagemNfe, NfeServiceDistribuidas } from '../../../shared/services/api/Financeiro/fiscaldistribuidas';
import dayjs from 'dayjs';
import { DialoDistribuida } from './DialogDistribuida';

interface IitensEntradaDialog {
  idresnfe: string;
  chave: string;
  xjust: string;
  tpevento: string;
}
export const ListagemDeNfeDistribuida: React.FC = () => {
  const theme = useTheme();
  const [searchParams, setSearchParams] = useSearchParams();
  const { debounce } = useDebounce();
  const [idnfe, setIdNfe] = useState('');
  const [nome, setNome] = useState('');
  const [open, setOpen] = useState(false);
  const [dadositens, setDadositens] = useState<IitensEntradaDialog>();
  const [data, setData] = useState('');
  const [valor, setValor] = useState(0);
  const [rows, setRows] = useState<IListagemNfe[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [openmsg, setOpenmsg] = useState(false);
  const [msg, setMsg] = useState('');
  const [tms, setTms] = useState<'warning' | 'error' | 'info' | 'success'>('warning');
  const ROWS_CACHE_KEY = 'nfeDistribuidas';
  const [rowsPerPage, setRowsPerPage] = useState(() => getRowsPerPageCache(ROWS_CACHE_KEY) ?? Environment.LIMITE_DE_LINHAS);
  const [containerHeight, setContainerHeight] = useState<number>();
  const tabelaRef = useRef<HTMLDivElement>(null);
  const rowHeightRef = useRef<number>(0);
  const headerHeightRef = useRef<number>(0);
  const footerHeightRef = useRef<number>(0);

  const calcularAlturaContainer = () => {
    const container = tabelaRef.current;
    if (!container) return;

    // Só é recalculado em um resize real da janela (não a cada render), para a parte superior não
    // ficar se reposicionando sozinha e gerando atualizações em loop na listagem.
    const BOTTOM_GAP = 16;
    const novaAltura = Math.max(200, window.innerHeight - container.getBoundingClientRect().top - BOTTOM_GAP);
    setContainerHeight(prev => (prev === novaAltura ? prev : novaAltura));
  };

  const calcularLimiteLinhas = () => {
    const container = tabelaRef.current;
    if (!container) return;

    const headerEl = container.querySelector('thead');
    const rowEl = rows.length > 0 ? container.querySelector('tbody tr') : null;
    const footerEl = container.querySelector('tfoot');

    if (headerEl) headerHeightRef.current = headerEl.getBoundingClientRect().height;
    if (rowEl) rowHeightRef.current = rowEl.getBoundingClientRect().height;
    if (footerEl) footerHeightRef.current = footerEl.getBoundingClientRect().height;

    if (!rowHeightRef.current || !containerHeight) return;

    const MIN_ROWS = 1;
    const MAX_ROWS = 100;
    const FOLGA_LINHAS = 1;
    // Usa o orçamento de altura calculado (containerHeight), não container.clientHeight:
    // quando a listagem tem poucos itens o container encolhe (height: 'auto'), e
    // clientHeight passaria a refletir só o conteúdo atual em vez do espaço disponível.
    const alturaDisponivel = containerHeight - headerHeightRef.current - footerHeightRef.current;
    const novoLimite = Math.min(
      MAX_ROWS,
      Math.max(MIN_ROWS, Math.floor(alturaDisponivel / rowHeightRef.current) - FOLGA_LINHAS)
    );

    setRowsPerPageCache(ROWS_CACHE_KEY, novoLimite);
    setRowsPerPage(prev => (prev === novoLimite ? prev : novoLimite));
  };

  useEffect(() => {
    calcularAlturaContainer();
    window.addEventListener('resize', calcularAlturaContainer);
    return () => window.removeEventListener('resize', calcularAlturaContainer);
  }, []);

  // Depende de totalCount (não de "rows") para não reagendar a cada refetch/página,
  // o que causava um looping de recálculo quando a altura medida variava por
  // arredondamento (ex: 1 item sem paginação, ou fetches acumulados ao voltar de outra aba).
  useEffect(() => {
    const id = setTimeout(calcularLimiteLinhas, 50);
    return () => clearTimeout(id);
  }, [totalCount, containerHeight]);

  const handleCloseMsg = (
    event?: React.SyntheticEvent | Event,
    reason?: SnackbarCloseReason,
  ) => {
    if (reason === 'clickaway') {
      return;
    }
    setOpenmsg(false);
  };
  const busca = useMemo(() => {
    return searchParams.get('busca') || '';
  }, [searchParams]);

  const basicselect = useMemo(() => {
    return searchParams.get('basicselect') || 'n.dhemi';
  }, [searchParams]);

  const pagina = useMemo(() => {
    return Number(searchParams.get('pagina') || '1');
  }, [searchParams]);

 
  useEffect(() => {
    setIsLoading(true);
    debounce(() => {
      NfeServiceDistribuidas.getAll(pagina, busca, basicselect, rowsPerPage)
        .then((result) => {
          setIsLoading(false);
          if (result instanceof Error) {
            setTms('error');
            setMsg(result.message);
            setOpenmsg(true);
          } else {
            setTotalCount(result.totalCount);
            setRows(result.data);
          }
        });
    });
  }, [busca, pagina, basicselect, open, rowsPerPage]);

  useEffect(() => {
    if (totalCount === 0) return;
    const totalPaginas = Math.max(1, Math.ceil(totalCount / rowsPerPage));
    if (pagina > totalPaginas) {
      setSearchParams({
        busca,
        basicselect,
        pagina: '1'
      }, { replace: true });
    }
  }, [rowsPerPage, totalCount]);
  const handleBuscarDados = (id: string) => {
    const foundItem = rows.find((listItem) => listItem.idresnfe === id);
    if (foundItem) {
      setNome(foundItem.xnome ?? '');
      setData(dayjs(foundItem.dhemi).format('DD/MM/YYYY'));
      setValor(Number(foundItem.vnfe || 0));

      const dadosConvertidos: IitensEntradaDialog = {
        idresnfe: foundItem.idresnfe,
        chave: foundItem.chnfe || '',
        xjust: '',
        tpevento: '',
      };
      setDadositens(dadosConvertidos);
    }
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };
  const formatarCpfCnpj = (cpfCnpj: string) => {
    const apenasNumeros = cpfCnpj.replace(/\D/g, '');
    if (apenasNumeros.length === 11) {
      return apenasNumeros.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
    } else if (apenasNumeros.length === 14) {
      return apenasNumeros.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/, '$1.$2.$3/$4-$5');
    }

    return cpfCnpj;
  };
  const getStatusNfe = (status: number) => {
    switch (status) {
      case 1:
        return {
          label: 'Autorizada',
          color: 'success' as const,
          iconColor: theme.palette.success.main,
          icon: 'check_circle'
        };
      case 2:
        return {
          label: 'Cancelada',
          color: 'error' as const,
          iconColor: theme.palette.error.main,
          icon: 'cancel'
        };
      case 3:
        return {
          label: 'Denegada',
          color: 'warning' as const,
          iconColor: theme.palette.warning.main,
          icon: 'warning'
        };
      case 4:
        return {
          label: 'Inutilizada',
          color: 'default' as const,
          iconColor: theme.palette.text.secondary,
          icon: 'block'
        };
      default:
        return {
          label: 'Pendente',
          color: 'info' as const,
          iconColor: theme.palette.info.main,
          icon: 'pending'
        };
    }
  };
  return (
    <LayoutBaseDePagina titulo="Listagem de NFE Distribuição">
      <Snackbar
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        open={openmsg}
        autoHideDuration={900}
        onClose={handleCloseMsg}
      >
        <Alert
          onClose={handleCloseMsg}
          severity={tms}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {msg}
        </Alert>
      </Snackbar>
      <Paper
        elevation={0}
        sx={{
          borderRadius: 1,
          overflow: 'hidden',
          border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
          backgroundColor: theme.palette.mode === 'dark'
            ? alpha(theme.palette.background.paper, 0.9)
            : theme.palette.background.paper,
        }}
      >
        <TableContainer
          ref={tabelaRef}
          sx={{
            // 'auto' quando a página atual não preenche rowsPerPage (única página curta ou
            // última página parcial de uma listagem paginada), para o fundo acompanhar a
            // quantidade real de itens em vez de esticar até o fim da tela.
            height: rows.length >= rowsPerPage
              ? (containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)')
              : 'auto',
            maxHeight: containerHeight ? `${containerHeight}px` : 'calc(100vh - 160px)',
            overflow: 'auto',
            scrollbarGutter: 'stable',
          }}
        >
        <Table
          size="medium"
          sx={{
            width: '100%',
            tableLayout: 'auto',
            minWidth: { xs: 480, sm: 600, md: 700 },
          }}
        >
          <TableHead
            sx={{
              '& th': {
                fontSize: { xs: '0.7rem', sm: '0.75rem' },
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.04em',
                color: theme.palette.text.secondary,
                backgroundColor: alpha(
                  theme.palette.primary.main,
                  0.06
                ),
                borderBottom: `1px solid ${alpha(
                  theme.palette.divider,
                  0.6
                )}`,
                padding: { xs: '8px 4px', sm: '12px 8px' },
              },
            }}
          >
            <TableRow>
              <TableCell align="left">Emissão</TableCell>
              <TableCell align="left">Emitente</TableCell>
              <TableCell align="left" sx={{ display: { xs: 'none', sm: 'table-cell' } }}>CNPJ</TableCell>
              <TableCell align="left" sx={{ display: { xs: 'none', md: 'table-cell' } }}>Inscr. Est.</TableCell>
              <TableCell align="right">Valor</TableCell>
            </TableRow>
          </TableHead>

          {isLoading && rows.length === 0 && (
            <TableBody>
              <LinhasEsqueletoTabela
                colunas={[
                  { width: '55%' },
                  { width: '80%' },
                  { width: '65%' },
                  { width: '55%' },
                  { align: 'right', width: '50%' },
                ]}
              />
            </TableBody>
          )}
          <TableBody>
            {!isLoading && rows.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Box py={4} display="flex" flexDirection="column" alignItems="center" gap={1}>
                    <Icon sx={{ fontSize: 40, color: theme.palette.text.secondary }}>
                      search_off
                    </Icon>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      fontWeight={500}
                    >
                      Nenhum registro encontrado
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {rows.map(row => (
              <TableRow
                key={row.idresnfe}
                hover
                sx={{
                  cursor: 'pointer',
                  backgroundColor: theme.palette.background.paper,
                  transition: 'all 0.25s ease',
                  '&:hover': {
                    backgroundColor: alpha(
                      theme.palette.primary.main,
                      0.12
                    ),
                    boxShadow: `0 6px 20px ${alpha(
                      theme.palette.common.black,
                      0.12
                    )}`,
                    transform: 'translateY(-2px)',
                  },
                }}
                onClick={() => handleBuscarDados(row.idresnfe)}
              >
                <TableCell sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' }, padding: { xs: '8px 4px', sm: '12px 8px' } }}>
                  {dayjs(row.dhemi).format('DD/MM/YYYY')}
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    fontWeight: 600,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: { xs: '110px', sm: '180px', md: '260px' },
                    padding: { xs: '8px 4px', sm: '12px 8px' },
                  }}
                >
                  <Tooltip title={row.xnome || ''} arrow>
                    <span>{row.xnome}</span>
                  </Tooltip>
                </TableCell>
                <TableCell
                  sx={{
                    fontSize: { xs: '0.7rem', sm: '0.75rem' },
                    fontFamily: 'monospace',
                    display: { xs: 'none', sm: 'table-cell' },
                    padding: { xs: '8px 4px', sm: '12px 8px' },
                  }}
                >
                  <Tooltip title={row.cnpj || ''} arrow>
                    <span>{formatarCpfCnpj(row.cnpj)}</span>
                  </Tooltip>
                </TableCell>

                <TableCell
                  sx={{
                    fontSize: '0.75rem',
                    fontFamily: 'monospace',
                    fontWeight: 600,
                    display: { xs: 'none', md: 'table-cell' },
                    padding: { xs: '8px 4px', sm: '12px 8px' },
                  }}
                >
                  {row.ie}
                </TableCell>

                <TableCell
                  align="right"
                  sx={{
                    fontSize: { xs: '0.75rem', sm: '0.8rem' },
                    fontWeight: 700,
                    color: theme.palette.success.dark,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    maxWidth: { xs: '90px', sm: 'none' },
                    padding: { xs: '8px 4px', sm: '12px 8px' },
                  }}
                >
                  <Tooltip
                    title={Number(row.vnfe || 0).toLocaleString('pt-BR', {
                      style: 'currency',
                      currency: 'BRL',
                    })}
                    arrow
                  >
                    <span>
                      {Number(row.vnfe || 0).toLocaleString('pt-BR', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </span>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={5}>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {totalCount > rowsPerPage && (
              <TableRow>
                <TableCell colSpan={5}>
                  <Box display="flex" justifyContent="center" py={2}>
                    <Pagination
                      page={pagina}
                      count={Math.ceil(
                        totalCount / rowsPerPage
                      )}
                      onChange={(_, newPage) =>
                        setSearchParams(
                          {
                            busca,
                            basicselect,
                            pagina: newPage.toString(),
                          },
                          { replace: true }
                        )
                      }
                      size="small"
                      color="primary"
                      showFirstButton
                      showLastButton
                    />
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableFooter>
        </Table>
        </TableContainer>
      </Paper>
      <DialoDistribuida
        id={idnfe}
        nome={nome}
        data={data}
        valor={valor}
        dados={dadositens}
        open={open}
        onClose={handleClose}
      />
    </LayoutBaseDePagina>
  );

};